import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  FilterBar,
  FormField,
  PageHeader,
  RowNumberCell,
  toast,
} from "../../components/ui";
import { Box, IconButton, MenuItem, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useDispatch } from "react-redux";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { OpenAPI, RingCentralService } from "../../api";
import React from "react";
import { RingCentralCallLogRequest } from "../../api/models/RingCentralCallLogRequest";
import { RingCentralTranscriptRequest } from "../../api/models/RingCentralTranscriptRequest";
import { RingCentralCallLogItemResponse } from "../../api/models/RingCentralCallLogItemRsponse";
import { RingCentralExtensionResponse } from "../../api/models/RingCentralExtensionRsponse";

const DEFAULT_ROWS_PER_PAGE = 25;
const RECORDINGS_LOOKBACK_DAYS = 90;

function buildCallLogRequest(extensionId?: string): RingCentralCallLogRequest {
  const dateFrom = new Date();
  dateFrom.setDate(dateFrom.getDate() - RECORDINGS_LOOKBACK_DAYS);

  return {
    extensionId: extensionId || undefined,
    withRecordingOnly: true,
    dateFrom: dateFrom.toISOString(),
    perPage: 1000,
  };
}

const TABLE_COLUMNS = [
  { id: "calleeNumber", label: "Callee number", sortable: false },
  { id: "calleeName", label: "Callee name", sortable: false },
  { id: "callerNumber", label: "Caller number", sortable: false },
  { id: "callerName", label: "Caller name", sortable: false },
  { id: "direction", label: "Direction", sortable: false },
  { id: "duration", label: "Duration", sortable: false },
  { id: "dateTime", label: "Date", sortable: false },
];

export default function RingCentralRecordings() {
  const [recordings, setRecordings] = useState<
    RingCentralCallLogItemResponse[]
  >([]);
  const [users, setUsers] = useState<RingCentralExtensionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterExtensionId, setFilterExtensionId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const dispatch = useDispatch();

  const loadRecordings = useCallback(
    (extensionId?: string) => {
      setLoading(true);
      dispatch(startSpinner());

      RingCentralService.getApiRingCentralCallLogs(
        buildCallLogRequest(extensionId)
      )
        .then((next) => {
          setRecordings(next ?? []);
          setPage(0);
        })
        .catch(() => {
          setRecordings([]);
          toast.error("Could not load RingCentral recordings.");
        })
        .finally(() => {
          setLoading(false);
          dispatch(stopSpinner());
        });
    },
    [dispatch]
  );

  const loadUsers = useCallback(() => {
    RingCentralService.getApiRingCentralUsers()
      .then(setUsers)
      .catch(() => {
        setUsers([]);
        toast.error("Could not load RingCentral users.");
      });
  }, []);

  useEffect(() => {
    loadUsers();
    loadRecordings();
  }, [loadUsers, loadRecordings]);

  const maxPage = Math.max(0, Math.ceil(recordings.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, maxPage]);

  const paginatedRecordings = useMemo(
    () =>
      recordings.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage
      ),
    [recordings, safePage, rowsPerPage]
  );

  const handleDownloadTranscript = (
    recording: RingCentralCallLogItemResponse,
    e?: React.MouseEvent
  ) => {
    e?.stopPropagation();
    dispatch(startSpinner());
    const request = {
      recordingContentUri: recording.recordingContentUri,
    } as RingCentralTranscriptRequest;

    fetch(`${OpenAPI.BASE}/api/RingCentral/downloadTranscript`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    })
      .then((response: any) => {
        if (!response.ok) {
          throw new Error("Download failed");
        }
        return response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `${recording.startTime}.docx`;
          document.body.appendChild(a);
          a.click();
          toast.success("Transcript downloaded.");
        });
      })
      .catch(() => {
        toast.error("Could not download transcript.");
      })
      .finally(() => dispatch(stopSpinner()));
  };

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      <PageHeader
        title="RingCentral recordings"
        subtitle="Browse recordings and download transcripts."
      />

      <Box sx={{ mb: 2 }}>
        <FilterBar
          hideFilterButton
          hideClearButton
          leading={
            <FormField
              select
              size="small"
              label="User"
              fullWidth={false}
              value={filterExtensionId}
              onChange={(e) => {
                const next = String(e.target.value);
                setFilterExtensionId(next);
                loadRecordings(next || undefined);
              }}
              sx={{
                width: { xs: "100%", sm: 240 },
                minWidth: { sm: 240 },
                bgcolor: "background.paper",
                borderRadius: 1,
              }}
            >
              <MenuItem value="">All users</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id ?? ""}>
                  {user.name}
                </MenuItem>
              ))}
            </FormField>
          }
        />
      </Box>

      <AppTable
        columns={TABLE_COLUMNS}
        showRowNumber
        loading={loading}
        loadingMessage="Loading recordings..."
        maxHeight="calc(100vh - 280px)"
        trailingHeadCells={
          <AppTableHeadCell align="right" sx={{ width: 72 }}>
            Transcript
          </AppTableHeadCell>
        }
        extraColumnCount={1}
        isEmpty={!loading && recordings.length === 0}
        emptyTitle="No recordings found"
        emptyDescription="Try another extension or check back later."
        pagination={{
          count: recordings.length,
          page: safePage,
          rowsPerPage,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {paginatedRecordings.map((recording, index) => (
          <AppTableRow
            key={recording.recordingContentUri || index}
            tabIndex={-1}
          >
            <RowNumberCell value={safePage * rowsPerPage + index + 1} />
            <AppTableCell>{recording.from || "—"}</AppTableCell>
            <AppTableCell>{recording.calleeName || "—"}</AppTableCell>
            <AppTableCell>{recording.to || "—"}</AppTableCell>
            <AppTableCell>{recording.callerName || "—"}</AppTableCell>
            <AppTableCell>{recording.direction || "—"}</AppTableCell>
            <AppTableCell>{recording.duration ?? "—"}</AppTableCell>
            <AppTableCell>
              {recording.startTime
                ? new Date(recording.startTime).toLocaleDateString("en-US")
                : "—"}
            </AppTableCell>
            <AppTableCell align="right">
              <Tooltip title="Download transcript">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => handleDownloadTranscript(recording, e)}
                  aria-label="Download transcript"
                >
                  <DownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>
    </Box>
  );
}
